// src/components/admin/flows/WorkflowList.js
import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useFlowAutomation } from '../../../contexts/FlowAutomationContext';
import flowAutomationService from '../../../services/flowAutomation.service';
import { useTheme } from '../../../contexts/ThemeContext';
import WorkflowCard from './components/WorkflowCard';
import WorkflowFilters from './components/WorkflowFilters';
import EmptyState from './components/EmptyState';

const WorkflowList = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const isDark = theme === 'dark';
    const {
        workflows, filters, setFilters,
        fetchWorkflows, deleteWorkflow, duplicateWorkflow, toggleStatus, loading
    } = useFlowAutomation();

    useEffect(() => {
        // Always re-fetch on mount (e.g., returning from builder)
        fetchWorkflows(filters);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const filteredWorkflows = useMemo(() => {
        return workflows.filter(wf => {
            if (filters.type && wf.type !== filters.type) return false;
            if (filters.status && wf.status !== filters.status) return false;
            if (filters.search) {
                const search = filters.search.toLowerCase();
                if (!wf.name?.toLowerCase().includes(search) && !wf.description?.toLowerCase().includes(search)) {
                    return false;
                }
            }
            return true;
        });
    }, [workflows, filters]);

    const handleEdit = (workflow) => {
        navigate(`/admin/flows/builder/${workflow.id}`);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this workflow?')) {
            await deleteWorkflow(id);
        }
    };

    const handleRun = async (id) => {
        const result = await flowAutomationService.triggerManualRun(id);
        if (result.success !== false) {
            alert('Workflow triggered successfully. Check the History tab for results.');
        } else {
            alert(`Failed to trigger: ${result.error || 'Unknown error'}`);
        }
    };

    if (!loading && workflows.length === 0) {
        return (
            <EmptyState
                title="No workflows yet"
                description="Create your first workflow to automate HR processes like onboarding, approvals, and task assignments."
                actionLabel="Create Workflow"
                onAction={() => navigate('/admin/flows/builder')}
            />
        );
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <WorkflowFilters filters={filters} onFilterChange={setFilters} />
                <button
                    onClick={() => navigate('/admin/flows/builder')}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors ml-4 flex-shrink-0"
                >
                    <Plus className="w-4 h-4" />
                    New Workflow
                </button>
            </div>

            {/* Grid */}
            {filteredWorkflows.length === 0 ? (
                <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    <p className="text-sm">No workflows match your filters</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filteredWorkflows.map((workflow) => (
                        <WorkflowCard
                            key={workflow.id}
                            workflow={workflow}
                            onEdit={handleEdit}
                            onDuplicate={duplicateWorkflow}
                            onToggleStatus={toggleStatus}
                            onDelete={handleDelete}
                            onRun={handleRun}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default WorkflowList;
